import moment from "moment";
import React from "react";
import { JobHistoryEntry } from "../../../models/JobHistory";
import EventTimeline from "./EventTimeline";
import {
	JOB_DOT_CLASS_BY_SOURCE,
	JOB_DOT_CLASS_BY_TYPE,
	JOB_TIMELINE_PREFIX,
	jobLabelValues,
	jobSubjectContactId,
	renderJobExtraRows,
	renderJobMeta,
} from "../../../pages/Jobs/jobTimeline.config";

const anna = { id: "u1", firstName: "Anna", lastName: "Andersson" };
const erik = { id: "u2", firstName: "Erik", lastName: "Eriksson" };

const entries: JobHistoryEntry[] = [
	{
		id: "9",
		type: "TIME_REPORT_ATTESTED_BY_CUSTOMER",
		source: "TIME_REPORT",
		date: moment().subtract(1, "hour").toISOString(),
		user: anna,
		timeReport: {
			id: "tr1",
			consultant: erik,
			shift: {
				id: "s1",
				start: moment().subtract(1, "day").set({ hour: 8, minute: 0 }).toISOString(),
				end: moment().subtract(1, "day").set({ hour: 16, minute: 0 }).toISOString(),
			},
		},
	},
	{
		id: "8",
		type: "TIME_REPORT_REPORTED",
		source: "TIME_REPORT",
		date: moment().subtract(3, "hours").toISOString(),
		user: erik,
		timeReport: {
			id: "tr1",
			consultant: erik,
			shift: {
				id: "s1",
				start: moment().subtract(1, "day").set({ hour: 8, minute: 0 }).toISOString(),
				end: moment().subtract(1, "day").set({ hour: 16, minute: 0 }).toISOString(),
			},
		},
	},
	{
		id: "7b",
		type: "JOB_DENIED",
		source: "JOB",
		date: moment().subtract(1, "day").set({ hour: 15, minute: 30 }).toISOString(),
		user: anna,
		// A change the user approved in the AI assistant: byline reads "Anna … via AI-assistenten".
		via: "ai-assistant",
		args: { denialReason: { key: "no_match", name: "Ingen matchande konsult" } },
	},
	{
		id: "7",
		type: "INVITATION_EXPIRED",
		source: "JOB",
		date: moment().subtract(1, "day").toISOString(),
		args: { consultant: erik },
	},
	{
		id: "6",
		type: "JOB_STATUS_CHANGED",
		source: "JOB",
		date: moment().subtract(2, "days").set({ hour: 14, minute: 12 }).toISOString(),
		user: anna,
		args: { from: "OPEN", to: "BOOKED" },
	},
	{
		id: "5",
		type: "ACCEPTED_APPLICATION_CREATED",
		source: "JOB",
		date: moment().subtract(2, "days").set({ hour: 14, minute: 12 }).toISOString(),
		user: anna,
		args: { consultant: erik },
	},
	{
		id: "4b",
		type: "SHIFT_REMOVED",
		source: "JOB",
		date: moment().subtract(2, "days").set({ hour: 10, minute: 41 }).toISOString(),
		user: anna,
		args: {
			shifts: [
				{
					id: "s9",
					start: moment().add(5, "days").set({ hour: 8, minute: 0 }).toISOString(),
					end: moment().add(5, "days").set({ hour: 16, minute: 0 }).toISOString(),
				},
				{
					id: "s10",
					start: moment().add(6, "days").set({ hour: 8, minute: 0 }).toISOString(),
					end: moment().add(6, "days").set({ hour: 12, minute: 0 }).toISOString(),
				},
			],
		},
	},
	{
		id: "4",
		type: "SHIFT_UPDATED",
		source: "JOB",
		date: moment().subtract(2, "days").set({ hour: 9, minute: 3 }).toISOString(),
		user: anna,
		args: {
			changes: [
				{
					field: "start",
					from: moment().add(3, "days").set({ hour: 8, minute: 0 }).toISOString(),
					to: moment().add(3, "days").set({ hour: 9, minute: 0 }).toISOString(),
				},
				{ field: "break", from: 30, to: 60 },
			],
		},
	},
	{
		id: "3",
		type: "JOB_UPDATED",
		source: "JOB",
		date: moment().subtract(3, "days").toISOString(),
		user: anna,
		args: {
			changes: [
				{ field: "description", from: "Lärare åk 3", to: "Lärare åk 3-4" },
				{ field: "hidden", from: false, to: true },
				{
					field: "requirements",
					from: { certifications: [{ typeKey: "police-extract" }], tags: [] },
					to: {
						certifications: [{ typeKey: "hlr" }],
						tags: [{ key: "night-shift" }],
					},
				},
				{
					field: "competence",
					from: { branch: "school", service: "grundskola-f-3" },
					to: { branch: "school", service: "fritidspedagog" },
				},
				{ field: "contactPersons", from: [], to: [] },
			],
		},
	},
	{
		id: "2",
		type: "JOB_UPDATED",
		source: "JOB",
		date: moment().subtract(3, "days").toISOString(),
		user: anna,
		// Legacy entry: raw payload args without `changes` → label-only row
		args: { description: "Lärare åk 3" } as any,
	},
	{
		id: "1",
		type: "JOB_CREATED",
		source: "JOB",
		date: moment().subtract(4, "days").toISOString(),
		user: anna,
	},
];

const requirementNames: Record<string, string> = {
	"police-extract": "Polisregisterutdrag",
	hlr: "HLR",
	"night-shift": "Nattpass",
};

const serviceNames: Record<string, string> = {
	"grundskola-f-3": "Grundskola F-3",
	fritidspedagog: "Fritidspedagog",
};

/**
 * Example for /component-showcase: the job flavour of the shared timeline,
 * showing the full range of entry shapes — time report milestones with shift
 * context, system events without a user, field-level diffs (incl. requirements
 * +/− and competence with resolved names), removed/added shift rows, legacy
 * label-only entries and an empty state.
 *
 * Wired exactly as JobHistoryTab wires it, so the showcase is a faithful check
 * that the job timeline still renders identically after the generalisation.
 */
const EventTimelineExample: React.FC = () => (
	<div className="flex max-w-xl flex-col gap-10">
		<EventTimeline
			entries={entries}
			i18nPrefix={JOB_TIMELINE_PREFIX}
			dotClassByType={JOB_DOT_CLASS_BY_TYPE}
			dotClassBySource={JOB_DOT_CLASS_BY_SOURCE}
			getLabelValues={jobLabelValues}
			getSubjectContactId={jobSubjectContactId}
			renderMeta={renderJobMeta}
			renderExtraRows={renderJobExtraRows}
			resolveRequirementName={(_kind, key) => requirementNames[key]}
			resolveCompetenceName={(_branch, service) => serviceNames[service]}
		/>

		<div>
			<h4 className="mb-2 text-sm font-semibold text-gray-700">Tom tidslinje</h4>
			<EventTimeline entries={[]} i18nPrefix={JOB_TIMELINE_PREFIX} />
		</div>
	</div>
);

export default EventTimelineExample;
